import { useEffect } from "react";
import socket from "../utilities/socket";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "../utilities/constant";

import { Storage } from "../utilities/setting";

const HEARTBEAT_INTERVAL_MS = 10000;

function createHeartbeatWorker() {
  if (typeof Worker === "undefined") {
    return null;
  }

  const workerScript = `
    let heartbeatTimer = null;

    self.onmessage = function(event) {
      const { type, interval } = event.data || {};

      if (type === "START") {
        if (heartbeatTimer) {
          clearInterval(heartbeatTimer);
        }

        heartbeatTimer = setInterval(function() {
          self.postMessage({ type: "HEARTBEAT" });
        }, interval);
      }

      if (type === "STOP" && heartbeatTimer) {
        clearInterval(heartbeatTimer);
        heartbeatTimer = null;
      }
    };
  `;

  const workerBlob = new Blob([workerScript], {
    type: "application/javascript",
  });
  const workerUrl = URL.createObjectURL(workerBlob);

  return {
    worker: new Worker(workerUrl),
    workerUrl,
  };
}

function useSocket(agentId) {
  const navigate = useNavigate();

  useEffect(() => {
    if (!agentId) return;

    console.log("Initializing socket for:", agentId);
    const handleConnect = () => {
      console.log("Connected:", socket.id);

      socket.emit("join", { agentId });
    };

    const handleConnectError = (err) => {
      console.log("Connection error:", err.message);
    };

    const handleForceLogout = async () => {
      console.log("Force logout received");

      Storage.clearAll();

      socket.disconnect();

      navigate(`/${ROUTES.LOGIN}`);
    };

    const handleDisconnect = (reason) => {
      console.log("Socket disconnected:", reason);
    };

    const handleReconnectAttempt = () => {
      console.log("Reconnecting...");
    };

    const handleReconnect = () => {
      console.log("Reconnected");
      socket.emit("join", { agentId });
    };

    const handleReconnectFailed = () => {
      console.log("Reconnect failed");
    };

    // connect event
    socket.on("connect", handleConnect);

    // error handling
    socket.on("connect_error", handleConnectError);

    // force logout
    socket.on("force_logout", handleForceLogout);

    // disconnect log
    socket.on("disconnect", handleDisconnect);

    // connect socket (only if not already)
    if (!socket.connected) {
      socket.connect();
    } else {
      socket.emit("join", { agentId });
    }

    socket.on("reconnect_attempt", handleReconnectAttempt);

    socket.on("reconnect", handleReconnect);

    socket.on("reconnect_failed", handleReconnectFailed);

    return () => {
      socket.off("connect", handleConnect);
      socket.off("force_logout", handleForceLogout);
      socket.off("disconnect", handleDisconnect);
      socket.off("connect_error", handleConnectError);
      socket.off("reconnect_attempt", handleReconnectAttempt);
      socket.off("reconnect", handleReconnect);
      socket.off("reconnect_failed", handleReconnectFailed);
    };
  }, [agentId, navigate]);

  useEffect(() => {
    if (!agentId) return;

    let intervalId = null;
    let heartbeatWorker = null;
    let heartbeatWorkerUrl = null;

    const sendHeartbeat = () => {
      if (!socket.connected) {
        return;
      }

      socket.emit("heartbeat", { agentId });
    };

    const handleWorkerMessage = (event) => {
      if (event.data?.type === "HEARTBEAT") {
        sendHeartbeat();
      }
    };

    const handleVisibilityResume = () => {
      sendHeartbeat();
    };

    sendHeartbeat();

    const workerData = createHeartbeatWorker();

    if (workerData) {
      heartbeatWorker = workerData.worker;
      heartbeatWorkerUrl = workerData.workerUrl;
      heartbeatWorker.addEventListener("message", handleWorkerMessage);
      heartbeatWorker.postMessage({
        type: "START",
        interval: HEARTBEAT_INTERVAL_MS,
      });
    } else {
      intervalId = setInterval(sendHeartbeat, HEARTBEAT_INTERVAL_MS);
    }

    window.addEventListener("focus", handleVisibilityResume);
    window.addEventListener("online", handleVisibilityResume);
    window.addEventListener("pageshow", handleVisibilityResume);
    document.addEventListener("visibilitychange", handleVisibilityResume);

    return () => {
      window.removeEventListener("focus", handleVisibilityResume);
      window.removeEventListener("online", handleVisibilityResume);
      window.removeEventListener("pageshow", handleVisibilityResume);
      document.removeEventListener("visibilitychange", handleVisibilityResume);

      if (intervalId) {
        clearInterval(intervalId);
      }

      if (heartbeatWorker) {
        heartbeatWorker.postMessage({ type: "STOP" });
        heartbeatWorker.removeEventListener("message", handleWorkerMessage);
        heartbeatWorker.terminate();
      }

      if (heartbeatWorkerUrl) {
        URL.revokeObjectURL(heartbeatWorkerUrl);
      }
    };
  }, [agentId]);
}

export default useSocket;